import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Select, TextInput } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import { errorText } from "../../../shared/toastHelper";

const NO_CODE = "";
const NEW_CODE = "__new__";
const BLANK_CODE = { code: "", name: "" };

const GeneralLedgerCodeSelect = ({ codes, value, onChange }) => {
  const { handleCreateGeneralLedgerCode } = useSeriesShowContext();
  const [addedCodes, setAddedCodes] = useState([]);
  const [newCode, setNewCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (codes == null) return null;

  const allCodes = [...codes, ...addedCodes.filter((added) => !codes.some((code) => code.id === added.id))];

  const options = [
    { value: NO_CODE, label: "Use the series' GL code" },
    ...allCodes.map((code) => ({ value: String(code.id), label: `${code.code} — ${code.name}` })),
    { value: NEW_CODE, label: "+ Add a new GL code…" },
  ];

  const handleSelect = (selected) => {
    if (selected === NEW_CODE) {
      setNewCode(BLANK_CODE);
      setError(null);
      return;
    }
    onChange(selected == null ? NO_CODE : String(selected));
  };

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await handleCreateGeneralLedgerCode({ code: newCode.code.trim(), name: newCode.name.trim() });
      setAddedCodes((current) => [...current, created]);
      onChange(String(created.id));
      setNewCode(null);
    } catch (e) {
      const messages = e && e.response && e.response.data;
      setError(Array.isArray(messages) ? messages.join(", ") : errorText(e, "Couldn't add that GL code."));
    } finally {
      setSaving(false);
    }
  };

  const canCreate = newCode && newCode.code.trim() !== "" && newCode.name.trim() !== "" && !saving;

  return (
    <div className="mb-3" data-testid="pass-general-ledger-code">
      <Select
        id="pass-general-ledger-code"
        name="general_ledger_code_id"
        label="GL code"
        options={options}
        value={value}
        onChange={handleSelect}
        hint="The account this product's sales revenue posts to. Fees and taxes post to their own codes."
      />
      {newCode ? (
        <div className="p-3 mt-2" data-testid="pass-new-general-ledger-code" style={{ border: "1px solid var(--sl-border-secondary)", borderRadius: "8px" }}>
          <p className="small mb-2">
            <strong>New GL code</strong> — this is added to your chart of accounts and becomes available everywhere you pick a GL code.
          </p>
          <div className="d-flex" style={{ gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <TextInput
                size="sm"
                id="pass-new-general-ledger-code-code"
                label="Code"
                placeholder="4100"
                value={newCode.code}
                disabled={saving}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextInput
                size="sm"
                id="pass-new-general-ledger-code-name"
                label="Name"
                placeholder="Season Pass Revenue"
                value={newCode.name}
                disabled={saving}
                onChange={(e) => setNewCode({ ...newCode, name: e.target.value })}
              />
            </div>
          </div>
          {error ? <p className="small text-danger mt-2 mb-0">{error}</p> : null}
          <div className="d-flex mt-2" style={{ gap: "8px" }}>
            <Button variant="primary" size="sm" disabled={!canCreate} onClick={handleCreate} data-testid="pass-new-general-ledger-code-save">
              Add GL code
            </Button>
            <Button variant="secondary" size="sm" disabled={saving} onClick={() => setNewCode(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

GeneralLedgerCodeSelect.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default GeneralLedgerCodeSelect;
